import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { useMyCategories, useMyScenarios, useMySkillAreas } from '../../../queries/Scenarios';
import type { Scenario } from '../../types/scenarios';
import { styleSheet } from './styles';

export interface ScenarioSelectorProps {
	selectedScenarioId: string | null;
	onSelectScenario: (scenarioId: string | null) => void;
}

/**
 * ScenarioSelector component allows users to select a training scenario
 * before starting a practice call. Scenarios are grouped by skill area.
 */
export const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({ selectedScenarioId, onSelectScenario }) => {
	// Track which categories and skill areas are expanded
	const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
	const [expandedSkillAreas, setExpandedSkillAreas] = useState<Set<string>>(new Set());

	// Fetch data - using my- endpoints for role-based filtering
	const { data: scenarios, isLoading: scenariosLoading } = useMyScenarios();
	const { data: skillAreas, isLoading: skillAreasLoading } = useMySkillAreas();
	const { data: categories, isLoading: categoriesLoading } = useMyCategories();

	// Handle loading state
	const isLoading = scenariosLoading || skillAreasLoading || categoriesLoading;
	if (isLoading) {
		return (
			<div className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>Select a Scenario to Practice</h3>
				<div className={css(styleSheet.loadingState)}>Loading scenarios...</div>
			</div>
		);
	}

	// Ensure data is in array format (handles various API response formats)
	const scenariosArray = Array.isArray(scenarios) ? scenarios : [];
	const skillAreasArray = Array.isArray(skillAreas) ? skillAreas : [];
	const categoriesArray = Array.isArray(categories) ? categories : [];

	// Handle empty state
	if (scenariosArray.length === 0) {
		return (
			<div className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>Select a Scenario to Practice</h3>
				<div className={css(styleSheet.emptyState)}>
					No scenarios available. Contact your administrator to set up training scenarios.
				</div>
			</div>
		);
	}

	// Group scenarios by skillArea, then skillAreas by category
	const scenariosBySkillArea = new Map<string, Scenario[]>();
	scenariosArray.forEach(scenario => {
		const existing = scenariosBySkillArea.get(scenario.skillAreaId) || [];
		scenariosBySkillArea.set(scenario.skillAreaId, [...existing, scenario]);
	});

	// Build category -> skillArea -> scenarios structure
	const categoriesWithData = categoriesArray
		.map(category => {
			const categorySkillAreas = skillAreasArray
				.filter(sa => sa.categoryId === category.id)
				.map(skillArea => ({
					skillArea,
					scenarios: scenariosBySkillArea.get(skillArea.id) || [],
				}))
				.filter(sa => sa.scenarios.length > 0);

			return {
				category,
				skillAreas: categorySkillAreas,
			};
		})
		.filter(cat => cat.skillAreas.length > 0);

	// Toggle category expansion
	const toggleCategory = (categoryId: string) => {
		const newExpanded = new Set(expandedCategories);
		if (newExpanded.has(categoryId)) {
			newExpanded.delete(categoryId);
		} else {
			newExpanded.add(categoryId);
		}
		setExpandedCategories(newExpanded);
	};

	// Toggle skill area expansion
	const toggleSkillArea = (skillAreaId: string) => {
		const newExpanded = new Set(expandedSkillAreas);
		if (newExpanded.has(skillAreaId)) {
			newExpanded.delete(skillAreaId);
		} else {
			newExpanded.add(skillAreaId);
		}
		setExpandedSkillAreas(newExpanded);
	};

	// Render a single scenario item
	const renderScenarioItem = (scenario: Scenario) => {
		const isSelected = selectedScenarioId === scenario.id;

		return (
			<div
				key={scenario.id}
				className={css(styleSheet.scenarioItem, isSelected && styleSheet.scenarioItemSelected)}
				onClick={() => onSelectScenario(scenario.id)}
			>
				<div className={css(styleSheet.scenarioContent)}>
					<div className={css(styleSheet.scenarioName)}>{scenario.name}</div>
					<div className={css(styleSheet.scenarioDescription)} title={scenario.description}>
						{scenario.description}
					</div>
				</div>
			</div>
		);
	};

	// Render category sections with nested skill areas
	const renderCategories = () => {
		return categoriesWithData.map(({ category, skillAreas: catSkillAreas }) => {
			const isCategoryExpanded = expandedCategories.has(category.id);

			return (
				<div key={category.id} className={css(styleSheet.categorySection)}>
					<div
						className={css(styleSheet.categoryHeader, isCategoryExpanded && styleSheet.categoryHeaderExpanded)}
						onClick={() => toggleCategory(category.id)}
					>
						<h4 className={css(styleSheet.categoryTitle)}>{category.name}</h4>
						<svg
							className={css(styleSheet.chevron, isCategoryExpanded && styleSheet.chevronExpanded)}
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						>
							<polyline points='9 18 15 12 9 6' />
						</svg>
					</div>
					{isCategoryExpanded && (
						<div className={css(styleSheet.categoryContent)}>
							{catSkillAreas.map(({ skillArea, scenarios: areaScenarios }) => {
								const isSkillAreaExpanded = expandedSkillAreas.has(skillArea.id);

								return (
									<div key={skillArea.id} className={css(styleSheet.skillAreaSection)}>
										<div
											className={css(
												styleSheet.skillAreaHeader,
												isSkillAreaExpanded && styleSheet.skillAreaHeaderExpanded
											)}
											onClick={() => toggleSkillArea(skillArea.id)}
										>
											<h5 className={css(styleSheet.skillAreaTitle)}>{skillArea.name}</h5>
											<svg
												className={css(styleSheet.chevron, isSkillAreaExpanded && styleSheet.chevronExpanded)}
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<polyline points='9 18 15 12 9 6' />
											</svg>
										</div>
										{isSkillAreaExpanded && (
											<div className={css(styleSheet.scenarioList)}>{areaScenarios.map(renderScenarioItem)}</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			);
		});
	};

	return (
		<div className={css(styleSheet.container)}>
			<h3 className={css(styleSheet.header)}>Select a Scenario to Practice</h3>
			{renderCategories()}
		</div>
	);
};
